import { useMemo } from 'react';

import { Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { focusStyles } from '@udacity/chakra-uds-theme';

import { useI18n } from '~/hooks/use-i18n';
import { isSelectOption } from '~/types/select-option';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

export function Pagination({
  currentPage,
  totalPages,
  pageIsZeroIndexed,
  onChange,
}: {
  currentPage: number;
  totalPages: number;
  pageIsZeroIndexed?: boolean;
  onChange: (page: number) => void;
}) {
  const { isRtl } = useI18n();
  const linkFocusStyles = focusStyles('primary.blue');
  const options = Array.from({ length: totalPages }, (_, i) => ({
    label: `Page ${i + 1}`,
    value: `${i + 1}`,
  }));

  const displayedPageNumbers = useMemo(() => {
    const currentDisplayedPageNumber = currentPage + 1;

    if (totalPages === 0) {
      return [1];
    }

    if (totalPages < 5) {
      return new Array(totalPages).fill(null).map((_, index) => index + 1);
    }

    if (currentDisplayedPageNumber < 5) {
      return [1, 2, 3, 4, 5];
    }

    if (currentDisplayedPageNumber >= 5 && currentDisplayedPageNumber > totalPages - 3) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    if (currentDisplayedPageNumber >= 5 && currentDisplayedPageNumber < totalPages) {
      return [currentDisplayedPageNumber - 1, currentDisplayedPageNumber, currentDisplayedPageNumber + 1];
    }

    return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }, [currentPage, totalPages]);

  function handlePageChange(newValue: unknown) {
    if (isSelectOption(newValue)) {
      onChange(pageIsZeroIndexed ? Number(newValue.value) - 1 : Number(newValue.value));
    }
  }

  const shouldDisplayFirstPageNumber = displayedPageNumbers[0] > 1;
  const shouldDisplayLastPageNumber = !displayedPageNumbers.includes(totalPages) && totalPages > 0;

  const PagesMenu = (
    <Menu>
      <MenuButton
        aria-label='Select a page of results'
        {...(linkFocusStyles as any)}
        borderRadius='2px'
        color='black'
        height='24px'
        minWidth='24px'
        paddingX='4px'
        transition='all 0.3s ease'
        _focusVisible={{
          ...linkFocusStyles._focusVisible,

          _before: {
            ...linkFocusStyles._focusVisible._before,

            width: '100%',
            height: '100%',
          },
        }}
        _hover={{
          color: 'primary.blue',
          background: 'supportBlue.10',
        }}
      >
        ...
      </MenuButton>
      <MenuList maxHeight='300px' overflow='auto' zIndex='99'>
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handlePageChange(option)}>
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );

  if (totalPages === 0) {
    return null;
  }

  return (
    <Flex alignItems='center' flex={1} gap='8px' justifyContent='center'>
      <IconButton
        aria-label='Show previous page of results'
        flexShrink={0}
        icon={isRtl ? <ChevronRightIcon h='20px' w='20px' /> : <ChevronLeftIcon h='20px' w='20px' />}
        isDisabled={(pageIsZeroIndexed ? currentPage + 1 : currentPage) === 1}
        marginRight='8px'
        size='sm'
        variant='outline'
        onClick={() => onChange(currentPage - 1)}
      />

      {totalPages > 0 && (
        <>
          {shouldDisplayFirstPageNumber && (
            <>
              <PaginationButton
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={1}
                onChange={onChange}
              />

              {PagesMenu}
            </>
          )}

          <Box display='flex' gap='8px'>
            {displayedPageNumbers.map((pageNumber) => (
              <PaginationButton
                key={pageNumber}
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={pageNumber}
                onChange={onChange}
              />
            ))}
          </Box>

          {shouldDisplayLastPageNumber && (
            <>
              {PagesMenu}

              <PaginationButton
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={totalPages}
                onChange={onChange}
              />
            </>
          )}
        </>
      )}

      <IconButton
        aria-label='Show next page of results'
        flexShrink={0}
        icon={isRtl ? <ChevronLeftIcon h='20px' w='20px' /> : <ChevronRightIcon h='20px' w='20px' />}
        isDisabled={(pageIsZeroIndexed && totalPages !== 0 ? currentPage + 1 : currentPage) === totalPages}
        marginLeft='8px'
        size='sm'
        variant='outline'
        onClick={() => onChange(currentPage + 1)}
      />
    </Flex>
  );
}

function PaginationButton({
  pageNumber,
  currentPage,
  pageIsZeroIndexed,
  onChange,
}: {
  pageNumber: number;
  currentPage: number;
  pageIsZeroIndexed?: boolean;
  onChange: (page: number) => void;
}) {
  const displayedCurrentPage = pageIsZeroIndexed ? currentPage + 1 : currentPage;
  const isCurrentPage = displayedCurrentPage === pageNumber;

  const handleClick = () => {
    const targetPage = pageIsZeroIndexed ? pageNumber - 1 : pageNumber;
    onChange(targetPage);
  };

  return (
    <Button
      key={pageNumber}
      size='xs'
      sx={isCurrentPage ? { bgColor: 'primary.blue', color: 'white !important' } : {}}
      variant='outline'
      onClick={handleClick}
    >
      {pageNumber}
    </Button>
  );
}
